import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  ProgressIndicator,
  RadixAlertDialog,
  RadixAlertDialogContent,
  RadixAlertDialogTitle,
} from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { AnimatedDots } from '@/shared';
import { DtoExtension } from '@/shared/constants/dto';
import { fetchAssetBlob } from '../editor-property-panel/ui/contents/asset/utils/fetch-asset-blob';

interface AssetDownloadProgressContextType {
  fetchAssetWithProgress: (
    publicUrl: string,
    name: string,
    extension?: DtoExtension | null,
  ) => Promise<Blob>;
}

const AssetDownloadProgressContext =
  createContext<AssetDownloadProgressContextType>({
    fetchAssetWithProgress: () => Promise.reject(new Error('Provider 없음')),
  });

interface AssetDownloadProgressProviderProps {
  children: ReactNode;
}

export const AssetDownloadProgressProvider = ({
  children,
}: AssetDownloadProgressProviderProps) => {
  const t = useTranslations();
  const [downloadState, setDownloadState] = useState<{
    name: string;
    progress: number;
  } | null>(null);

  const fetchAssetWithProgress = useCallback(
    async (
      publicUrl: string,
      name: string,
      extension?: DtoExtension | null,
    ): Promise<Blob> => {
      setDownloadState({ name, progress: 0 });
      try {
        const blob = await fetchAssetBlob(
          { publicUrl, extension },
          (progress) =>
            setDownloadState((prev) => (prev ? { ...prev, progress } : null)),
        );
        return blob;
      } finally {
        setDownloadState(null);
      }
    },
    [],
  );

  const value = useMemo(
    () => ({ fetchAssetWithProgress }),
    [fetchAssetWithProgress],
  );

  return (
    <AssetDownloadProgressContext.Provider value={value}>
      {children}
      {downloadState && (
        <RadixAlertDialog open={true}>
          <RadixAlertDialogTitle />
          <RadixAlertDialogContent
            className="flex max-w-[520px] flex-col gap-2xs p-md"
            aria-describedby={undefined}
          >
            <div className="flex flex-col items-start gap-4xs">
              <div className="text-title-large">
                {t('Editor.asset-panel.loading-panel.title')}
                <AnimatedDots />
              </div>
              <div className="text-body-medium">
                {t('Editor.asset-panel.loading-panel.description', {
                  title: downloadState.name,
                })}
              </div>
            </div>
            <ProgressIndicator
              value={downloadState.progress}
              unit={'%'}
            />
          </RadixAlertDialogContent>
        </RadixAlertDialog>
      )}
    </AssetDownloadProgressContext.Provider>
  );
};

export const useAssetDownloadProgress = () => {
  return useContext(AssetDownloadProgressContext);
};
